import { createStore } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { deleteAuthHeader, setAuthHeader } from '~/api/clients';
import { queryClient } from '~/api/query/queryClient';
import { normalizeTokenType } from '~/api/utils';
import { clearSession } from '~/utils/storage';

import type { ADCJWTPayload, UserRole } from './types';

type NormalizedUserInfo = {
  userID: string;
  role: UserRole;
  idnId?: string;
  idnName?: string;
  departmentId?: string;
  departmentName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

type AuthState = {
  accessToken: string | null;
  tokenType: string | null;
  decodedJWTPayloard?: ADCJWTPayload | null;
  userInfo: NormalizedUserInfo | null;
};

const authAtom = atomWithStorage<AuthState>(
  'auth',
  {
    accessToken: null,
    tokenType: null,
    decodedJWTPayloard: null,
    userInfo: null,
  },
  undefined,
  { getOnInit: true },
);

export const authStore = createStore();

export const authState = {
  get: () => authStore.get(authAtom),
  set: (auth: AuthState) => {
    authStore.set(authAtom, auth);
    // Set the authorization header for all API clients
    setAuthHeader(
      'Authorization',
      `${normalizeTokenType(auth.tokenType)} ${auth.accessToken ?? ''}`,
    );
  },
  reset: () => {
    // Clear session storage
    clearSession();
    // Reset the auth state
    authStore.set(authAtom, {
      accessToken: null,
      tokenType: null,
      decodedJWTPayloard: null,
      userInfo: null,
    });

    // Invalidate any cached data
    queryClient.clear();
    // Clear the authorization header for all API clients
    deleteAuthHeader('Authorization');
  },
  sub: (callback: (auth: AuthState) => void) => {
    const unsub = authStore.sub(authAtom, () => {
      callback(authStore.get(authAtom));
    });

    return () => {
      unsub();
    };
  },
  /**
   * Returns the idnID from the current userInfo, or null if not present.
   */
  getIDNId: () => authStore.get(authAtom).userInfo?.idnId ?? null,
  /**
   * Returns the departmentId from the current userInfo, or null if not present.
   */
  getDepartmentId: () => authStore.get(authAtom).userInfo?.departmentId ?? null,
  /**
   * Returns the role from the current userInfo, or null if not present.
   */
  getUserRole: () => authStore.get(authAtom).userInfo?.role ?? null,
} as const;
